import React, { useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Lightbulb, Ruler, Zap, Cable, MapPin } from 'lucide-react';

const STORAGE_KEY = 'sno_lighting_parameter_defaults';

export default function LightParameters({ parameters, onChange }) {
  // Load defaults from localStorage on mount if parameters are empty
  useEffect(() => {
    if (!parameters || Object.keys(parameters).length === 0) {
      const savedDefaults = localStorage.getItem(STORAGE_KEY);
      if (savedDefaults) {
        try {
          const defaults = JSON.parse(savedDefaults);
          onChange(defaults);
        } catch (e) {
          console.error('Failed to load parameter defaults:', e);
        }
      }
    }
  }, []);

  const updateParam = (key, value) => {
    const newParams = { ...parameters, [key]: value };
    onChange(newParams);
    // Save to localStorage as defaults
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newParams));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Ruler className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Light Spacing</Label>
        </div>
        <div className="space-y-2">
          <Slider
            value={[parameters.light_spacing_inches || 12]}
            onValueChange={([v]) => updateParam('light_spacing_inches', v)}
            min={1}
            max={32}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500">
            <span>1"</span>
            <span className="font-medium text-slate-900">{parameters.light_spacing_inches || 12}" spacing</span>
            <span>32"</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Light Type</Label>
        </div>
        <Select
          value={parameters.light_type || 'rgbic_puck'}
          onValueChange={(v) => updateParam('light_type', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rgbw_puck">RGBW Puck Lights</SelectItem>
            <SelectItem value="rgbic_puck">RGBIC Puck Lights</SelectItem>
            <SelectItem value="ws2811_pixels">WS2811 Pixels</SelectItem>
            <SelectItem value="rgb_strip">RGB LED Strip</SelectItem>
            <SelectItem value="warm_white">Warm White Pucks</SelectItem>
            <SelectItem value="cool_white">Cool White Pucks</SelectItem>
            <SelectItem value="addressable">Addressable RGB</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Operating Voltage</Label>
        </div>
        <Select
          value={parameters.voltage || '24v'}
          onValueChange={(v) => updateParam('voltage', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5v">5V (WS2812B, SK6812)</SelectItem>
            <SelectItem value="12v">12V (WS2811, Standard)</SelectItem>
            <SelectItem value="24v">24V (Commercial)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Cable className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Mounting Method</Label>
        </div>
        <Select
          value={parameters.mounting_method || 'adhesive_screw'}
          onValueChange={(v) => updateParam('mounting_method', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="track">Aluminum Track</SelectItem>
            <SelectItem value="clips">Mounting Clips</SelectItem>
            <SelectItem value="adhesive">Adhesive Tape</SelectItem>
            <SelectItem value="adhesive_screw">Adhesive Tape & Screw</SelectItem>
            <SelectItem value="channel">Recessed Channel</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Transformer Wattage</Label>
        </div>
        <Select
          value={String(parameters.transformer_watts || 36)}
          onValueChange={(v) => updateParam('transformer_watts', parseInt(v))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="36">36W</SelectItem>
            <SelectItem value="60">60W</SelectItem>
            <SelectItem value="100">100W</SelectItem>
            <SelectItem value="150">150W</SelectItem>
            <SelectItem value="200">200W</SelectItem>
            <SelectItem value="300">300W</SelectItem>
            <SelectItem value="500">500W</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Cable className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Wire Gauge</Label>
        </div>
        <Select
          value={parameters.wire_gauge || '18awg'}
          onValueChange={(v) => updateParam('wire_gauge', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="22awg">22 AWG (0.64mm)</SelectItem>
            <SelectItem value="20awg">20 AWG (0.81mm)</SelectItem>
            <SelectItem value="18awg">18 AWG (1.02mm)</SelectItem>
            <SelectItem value="16awg">16 AWG (1.29mm)</SelectItem>
            <SelectItem value="14awg">14 AWG (1.63mm)</SelectItem>
            <SelectItem value="12awg">12 AWG (2.05mm)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Power Source Location</Label>
        </div>
        <Input
          value={parameters.power_source_location || ''}
          onChange={(e) => updateParam('power_source_location', e.target.value)}
          placeholder="e.g., Garage outlet, Front porch"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Ruler className="w-4 h-4 text-amber-600" />
          <Label className="text-sm font-medium">Calibration (Pixels per Foot)</Label>
        </div>
        <div className="space-y-2">
          <Slider
            value={[parameters.pixels_per_foot || 10]}
            onValueChange={([v]) => updateParam('pixels_per_foot', v)}
            min={5}
            max={30}
            step={1}
            className="w-full"
          />
          <p className="text-xs text-slate-500">
            Adjust to match image scale: <span className="font-medium">{parameters.pixels_per_foot || 10} px = 1 ft</span>
          </p>
        </div>
      </div>
    </div>
  );
}